import { FC, ReactNode } from 'react';
import { PiChatPanel } from '../PiChatPanel';
import type { ToolItem } from '../PiChatLog';
import { FileTree } from '../fileTree/FileTree';
import { RestoreEventRow } from '../versions/RestoreEventRow';
import { VersionActions } from '../versions/VersionActions';
import { VersionStatus } from '../versions/VersionStatus';
import { versionQaIds } from '../versions/qaIds';
import {
  selectCanRedo,
  selectCanSend,
  selectCanUndo,
  selectCurrentFiles,
  selectIsBusy,
  selectIsRestoring,
  selectVersionPosition,
} from '../versions/versionsReducer';
import type { BuilderChat } from './useBuilderChat';

export interface BuilderChatPanelProps {
  chat: BuilderChat;
  qaPrefix: string;
  title: string;
  label: string;
  placeholder: string;
  emptyPrompt: string;
  onClose: () => void;
  extraActions?: ReactNode;
  aboveFileTree?: ReactNode;
  fileTreeDefaultOpen?: boolean;
  renderToolRow?: (item: ToolItem) => ReactNode;
  groupsTool?: (item: ToolItem) => boolean;
  className?: string;
}

export const BuilderChatPanel: FC<BuilderChatPanelProps> = ({
  chat,
  qaPrefix,
  title,
  label,
  placeholder,
  emptyPrompt,
  onClose,
  extraActions,
  aboveFileTree,
  fileTreeDefaultOpen,
  renderToolRow,
  groupsTool,
  className,
}) => {
  const {
    socket,
    versions,
    restoreEvents,
    panelState,
    connect,
    undo,
    redo,
    chatPhase,
    isLoading,
    loadingRevealed,
  } = chat;
  const restoreEventQaId = versionQaIds(qaPrefix).restoreEvent;

  return (
    <PiChatPanel
      className={className}
      chat={socket}
      phase={chatPhase}
      loadingRevealed={loadingRevealed}
      canSend={selectCanSend(panelState)}
      busy={selectIsBusy(panelState)}
      onClose={onClose}
      title={title}
      label={label}
      placeholder={placeholder}
      emptyPrompt={emptyPrompt}
      status={
        <VersionStatus
          qaPrefix={qaPrefix}
          isRestoring={selectIsRestoring(panelState)}
          reconnecting={socket.reconnecting}
          isLoading={isLoading}
          position={selectVersionPosition(versions)}
        />
      }
      actions={
        <>
          <VersionActions
            qaPrefix={qaPrefix}
            canUndo={selectCanUndo(panelState)}
            canRedo={selectCanRedo(panelState)}
            onUndo={undo}
            onRedo={redo}
          />
          {extraActions}
        </>
      }
      onReconnect={connect}
      aboveLog={
        <>
          {aboveFileTree}
          <FileTree
            qaPrefix={qaPrefix}
            files={selectCurrentFiles(versions)}
            isLoading={isLoading}
            loadingRevealed={loadingRevealed}
            defaultOpen={fileTreeDefaultOpen}
          />
        </>
      }
      events={restoreEvents}
      renderEvent={(event) => (
        <RestoreEventRow event={event} qaId={restoreEventQaId} />
      )}
      renderToolRow={renderToolRow}
      groupsTool={groupsTool}
    />
  );
};
